import React, { useState, useEffect, useCallback, useRef } from 'react';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Header = ({ onDateChange }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterType, setDateFilterType] = useState('Redemption');
  
  // Use refs to always have current values in datepicker callbacks
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const onDateChangeRef = useRef(onDateChange);
  
  // Keep refs in sync with state
  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);
  
  useEffect(() => {
    endDateRef.current = endDate;
  }, [endDate]);

  useEffect(() => {
    onDateChangeRef.current = onDateChange;
  }, [onDateChange]);

  const handleDateFilterTypeChange = (nextType) => {
    if (nextType === dateFilterType) {
      return;
    }
    setDateFilterType(nextType);
    if (onDateChangeRef.current) {
      onDateChangeRef.current({ dateFilterType: nextType });
    }
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = MONTH_NAMES[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseManualDate = (dateStr) => {
    if (!dateStr) return null;

    const monthNameMatch = dateStr.trim().match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
    if (monthNameMatch) {
      const day = parseInt(monthNameMatch[1], 10);
      const month = MONTH_NAMES.findIndex((name) => name.toLowerCase() === monthNameMatch[2].toLowerCase());
      const year = parseInt(monthNameMatch[3], 10);
      if (day >= 1 && day <= 31 && month >= 0 && year >= 1900 && year <= 2100) {
        return new Date(year, month, day);
      }
    }
    
    const ddmmyyMatch = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
    if (ddmmyyMatch) {
      let [, day, month, year] = ddmmyyMatch;
      day = parseInt(day, 10);
      month = parseInt(month, 10) - 1;
      year = parseInt(year, 10);
      
      if (year < 100) {
        year += 2000;
      }
      
      if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
        return new Date(year, month, day);
      }
    }
    
    return null;
  };

  const handleCalendarClick = (pickerType) => {
    if (pickerType === 'start' && window.startDatePicker && window.startDatePicker.manualOpen) {
      window.startDatePicker.manualOpen();
    } else if (pickerType === 'end' && window.endDatePicker && window.endDatePicker.manualOpen) {
      window.endDatePicker.manualOpen();
    }
  };

  const handleDateChange = useCallback((dateObj) => {
    if (onDateChangeRef.current) {
      onDateChangeRef.current(dateObj);
    }
  }, []);

  useEffect(() => {
    if (window.M && window.M.Datepicker) {
      const startDateElem = document.getElementById('start-date');
      let startDateInstance = null;
      if (startDateElem) {
        startDateInstance = window.M.Datepicker.init(startDateElem, {
          format: 'dd-mmm-yyyy',
          autoClose: true,
          showClearBtn: false,
          container: 'body',
          onSelect: (date) => {
            const formattedDate = formatDate(date);
            setStartDate(formattedDate);
            if (onDateChangeRef.current) {
              onDateChangeRef.current({ startDate: formattedDate, endDate: endDateRef.current });
            }
          }
        });

        const originalOpen = startDateInstance.open;
        let manualOpen = false;
        
        startDateInstance.open = function() {
          if (manualOpen) {
            originalOpen.call(this);
            manualOpen = false;
          }
        };
        
        startDateInstance.manualOpen = function() {
          manualOpen = true;
          originalOpen.call(this);
        };
      }

      const endDateElem = document.getElementById('end-date');
      let endDateInstance = null;
      if (endDateElem) {
        endDateInstance = window.M.Datepicker.init(endDateElem, {
          format: 'dd-mmm-yyyy',
          autoClose: true,
          showClearBtn: false,
          container: 'body',
          onSelect: (date) => {
            const formattedDate = formatDate(date);
            setEndDate(formattedDate);
            if (onDateChangeRef.current) {
              onDateChangeRef.current({ startDate: startDateRef.current, endDate: formattedDate });
            }
          }
        });

        const originalEndOpen = endDateInstance.open;
        let manualEndOpen = false;
        
        endDateInstance.open = function() {
          if (manualEndOpen) {
            originalEndOpen.call(this);
            manualEndOpen = false;
          }
        };
        
        endDateInstance.manualOpen = function() {
          manualEndOpen = true;
          originalEndOpen.call(this);
        };
      }

      window.startDatePicker = startDateInstance;
      window.endDatePicker = endDateInstance;

      return () => {
        if (startDateInstance && startDateInstance.destroy) {
          startDateInstance.destroy();
        }
        if (endDateInstance && endDateInstance.destroy) {
          endDateInstance.destroy();
        }
        window.startDatePicker = null;
        window.endDatePicker = null;
      };
    }
  }, []);

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const handleStartDateFinish = (inputValue) => {
    const parsedDate = parseManualDate(inputValue);
    if (parsedDate) {
      const formattedDate = formatDate(parsedDate);
      setStartDate(formattedDate);
      handleDateChange({ startDate: formattedDate, endDate: endDateRef.current });
    } else {
      handleDateChange({ startDate: inputValue, endDate: endDateRef.current });
    }
  };

  const handleEndDateFinish = (inputValue) => {
    const parsedDate = parseManualDate(inputValue);
    if (parsedDate) {
      const formattedDate = formatDate(parsedDate);
      setEndDate(formattedDate);
      handleDateChange({ startDate: startDateRef.current, endDate: formattedDate });
    } else {
      handleDateChange({ startDate: startDateRef.current, endDate: inputValue });
    }
  };

  const handleStartDateKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleStartDateFinish(e.target.value);
      e.target.blur();
    }
  };

  const handleEndDateKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleEndDateFinish(e.target.value);
      e.target.blur();
    }
  };

  const handleStartDateBlur = (e) => {
    handleStartDateFinish(e.target.value);
  };

  const handleEndDateBlur = (e) => {
    handleEndDateFinish(e.target.value);
  };

  const clearStartDate = () => {
    setStartDate('');
    handleDateChange({ startDate: '', endDate });
  };

  const clearEndDate = () => {
    setEndDate('');
    handleDateChange({ startDate, endDate: '' });
  };

  const resetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setDateFilterType('Redemption');
    handleDateChange({ startDate: '', endDate: '', dateFilterType: 'Redemption' });
  };

  const dateTypeLabel = dateFilterType === 'Moderation' ? 'Moderation Date' : 'Redemption Date';
  const hasDateRange = Boolean(startDate || endDate);
  const isSameDay = Boolean(startDate && endDate && startDate === endDate);

  return (
    <>
      {/* Header Banner */}
      <div className="header-banner">
        <div className="banner-content">
          {/* Cat Paw Decorations */}
          <div className="paw-print paw-1">🐾</div>
          <div className="paw-print paw-2">🐾</div>
          <div className="paw-print paw-3">🐾</div>
          <div className="paw-print paw-4">🐾</div>
          <div className="paw-print paw-5">🐾</div>
          <div className="paw-print paw-6">🐾</div>
          <div className="paw-print paw-7">🐾</div>
          <div className="paw-print paw-8">🐾</div>
          <div className="paw-print paw-9">🐾</div>
          <div className="paw-print paw-10">🐾</div>
          
          {/* Main Content */}
          <div className="banner-text-container">
            <div className="banner-brand">
              <h1>HILL'S SCIENCE</h1>
            </div>
            <div className="banner-subtitle">
              <span className="subtitle-icon">📊</span>
              <p>TILL SLIP & CLAIMS INSIGHTS</p>
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="banner-accent-line"></div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="container">
        <div className="section-box date-range-container">
          <div className="date-filter-header">
            <div className="date-filter-header-icon" aria-hidden="true">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <h2 className="date-filter-title">Filter by date</h2>
          </div>

          <div className="date-filter-body">
            <div className="date-filter-step">
              <h3 className="date-filter-step-title">1. Choose which date type to filter</h3>
              <div className="date-filter-type-buttons">
                <button
                  type="button"
                  className={`date-filter-type-btn${dateFilterType === 'Redemption' ? ' active' : ''}`}
                  aria-pressed={dateFilterType === 'Redemption'}
                  onClick={() => handleDateFilterTypeChange('Redemption')}
                >
                  <i className="fas fa-gift" aria-hidden="true"></i>
                  Redemption Date
                </button>
                <button
                  type="button"
                  className={`date-filter-type-btn${dateFilterType === 'Moderation' ? ' active' : ''}`}
                  aria-pressed={dateFilterType === 'Moderation'}
                  onClick={() => handleDateFilterTypeChange('Moderation')}
                >
                  <i className="fas fa-shield-halved" aria-hidden="true"></i>
                  Moderation Date
                </button>
              </div>
              <p className="date-filter-help">The date range below will apply to the selected date type.</p>
            </div>

            <div className="date-filter-divider" aria-hidden="true"></div>

            <div className="date-filter-step">
              <h3 className="date-filter-step-title">2. Select date range</h3>
              <div className="date-filter-inputs">
                <div className="date-filter-field">
                  <label htmlFor="start-date">From</label>
                  <div className={`date-filter-input-wrap${startDate ? ' has-value' : ''}`}>
                    <input
                      type="text"
                      id="start-date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      onKeyPress={handleStartDateKeyPress}
                      onBlur={handleStartDateBlur}
                      placeholder="DD-MM-YY or click calendar"
                    />
                    <button
                      type="button"
                      className="date-filter-icon-btn"
                      onClick={() => handleCalendarClick('start')}
                      title="Open calendar"
                      aria-label="Open from date calendar"
                    >
                      <i className="fas fa-calendar-alt" aria-hidden="true"></i>
                    </button>
                    {startDate && (
                      <button
                        type="button"
                        className="date-filter-clear-btn"
                        onClick={clearStartDate}
                        title="Clear start date"
                        aria-label="Clear from date"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <div className="date-filter-field">
                  <label htmlFor="end-date">To</label>
                  <div className={`date-filter-input-wrap${endDate ? ' has-value' : ''}`}>
                    <input
                      type="text"
                      id="end-date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      onKeyPress={handleEndDateKeyPress}
                      onBlur={handleEndDateBlur}
                      placeholder="DD-MM-YY or click calendar"
                    />
                    <button
                      type="button"
                      className="date-filter-icon-btn"
                      onClick={() => handleCalendarClick('end')}
                      title="Open calendar"
                      aria-label="Open to date calendar"
                    >
                      <i className="fas fa-calendar-alt" aria-hidden="true"></i>
                    </button>
                    {endDate && (
                      <button
                        type="button"
                        className="date-filter-clear-btn"
                        onClick={clearEndDate}
                        title="Clear end date"
                        aria-label="Clear to date"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="date-filter-footer">
            <p className="date-filter-summary">
              <i className="fas fa-info-circle" aria-hidden="true"></i>
              <span>
                {isSameDay && (
                  <>Filtering results by <strong>{dateTypeLabel}</strong> on <strong>{startDate}</strong>.</>
                )}
                {!isSameDay && startDate && endDate && (
                  <>Filtering results by <strong>{dateTypeLabel}</strong> from <strong>{startDate}</strong> to <strong>{endDate}</strong>.</>
                )}
                {!isSameDay && startDate && !endDate && (
                  <>Filtering results by <strong>{dateTypeLabel}</strong> from <strong>{startDate}</strong>.</>
                )}
                {!isSameDay && !startDate && endDate && (
                  <>Filtering results by <strong>{dateTypeLabel}</strong> up to <strong>{endDate}</strong>.</>
                )}
                {!hasDateRange && (
                  <>Showing all results by <strong>{dateTypeLabel}</strong>. Select a date range to narrow results.</>
                )}
              </span>
            </p>
            <button
              type="button"
              className="date-filter-reset"
              onClick={resetDateFilter}
              disabled={!hasDateRange && dateFilterType === 'Redemption'}
            >
              <i className="fas fa-sync-alt" aria-hidden="true"></i>
              Reset
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
